import { ThemeEvents } from '@theme/events';

/**
 * Removes a claimed free gift once the cart no longer earns it.
 *
 * The gift discount function stops making the gift free below threshold but
 * can't remove the line, so without this it would sit there at full price.
 * After every cart change this re-reads /cart.js and removes the gift line
 * once it's no longer free (the discount dropped it) and the cart is under
 * the threshold. A gift that's still $0 is never removed.
 *
 * "The cart" here is what the shopper is actually paying: the cart total
 * after every discount, leaving out the gift line itself (same figure as
 * snippets/cart-gift-paid-cents.liquid).
 *
 * Replaces the compiled components/cart-gift-guard.ts bundle (which can't be
 * rebuilt safely, see snippets/vite-tag.liquid).
 */

function readConfig() {
  const script = document.querySelector('[data-cart-gift-guard-config]');
  if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

  try {
    const parsed = JSON.parse(script.textContent);
    if (!Array.isArray(parsed.giftProductIds) || parsed.giftProductIds.length === 0) return null;
    return { thresholdCents: Number(parsed.thresholdCents) || 0, giftProductIds: parsed.giftProductIds.map(String) };
  } catch {
    return null;
  }
}

/**
 * @param {{product_id: number, properties?: Record<string, string> | null, final_line_price: number}} line
 * @param {string[]} giftProductIds
 */
function isClaimedGiftLine(line, giftProductIds) {
  if (!giftProductIds.includes(String(line.product_id))) return false;
  return line.properties?._free_gift === 'true' || line.final_line_price === 0;
}

let checking = false;

async function checkGiftEligibility() {
  if (checking) return;
  const config = readConfig();
  if (!config || config.thresholdCents <= 0) return;

  checking = true;
  try {
    let cart;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      cart = await response.json();
    } catch {
      return;
    }

    const items = cart?.items;
    if (!items?.length) return;

    const giftLineIndex = items.findIndex((line) => isClaimedGiftLine(line, config.giftProductIds));
    if (giftLineIndex === -1) return;

    // Still free means the gift discount still covers it: leave it alone.
    // Only a gift that has gone back to full price gets removed. Without
    // this, a free gift that bumps the cart into a bigger volume tier drops
    // the total just under the threshold, gets removed, the cart goes back
    // over and offers it again: the "add, then it disappears" loop.
    if (items[giftLineIndex].final_line_price === 0) return;

    const giftCents = items.reduce(
      (sum, line) => (line.properties?._free_gift === 'true' ? sum + line.final_line_price : sum),
      0
    );
    const paidCents = (cart.total_price ?? 0) - giftCents;
    if (paidCents >= config.thresholdCents) return;

    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent && typeof cartItemsComponent.onLineItemRemove === 'function') {
      cartItemsComponent.onLineItemRemove(giftLineIndex + 1);
    }
  } finally {
    checking = false;
  }
}

if (!window.__cartGiftGuardBound) {
  window.__cartGiftGuardBound = true;
  document.addEventListener(ThemeEvents.cartUpdate, checkGiftEligibility);
  // Also catches a gift left over from an earlier visit (e.g. the cart was
  // lowered somewhere that doesn't fire cart:update, then the page reloaded).
  checkGiftEligibility();
}
